import { generateHash } from '../../../utils/caching/common/key-generator';
import { type CacheAnalyticsCallback, promiseCache } from '../../../utils/caching/persist-refresh';
import { DegradablePromise, type DegradationCallback } from '../../utils/degradable-promise';

import {
	ONE_DAY_MS_TTL,
	ONE_HOUR_MS_TTL,
	SEARCH_PERSONALIZATION_SMARTLINK_USAGE_AND_TOP_APPS_SITE_CACHE_KEY,
	SEVEN_DAY_MS_TTL,
	SMARTLINK_USAGE_AND_TOP_APPS_TRAIT,
} from './constants';
import { getSmartlinkSiteTraitsUrl } from './get-smartlink-site-traits-url';
import type { Trait, Traits } from './types';

/**
 * Parsed shape of the site-scoped TAP trait
 * `rovo_nudge_end_user_rendered_smartlinks_percent_and_most_used_apps`.
 *
 * Raw value is a CSV string `"<percent>,<app1>,<app2>,..."` — see
 * {@link parseSmartlinkUsageAndTopAppsTrait} for the parser.
 *
 * Example raw value: `"82,google-calendar,zendesk,drive,slack,miro,figma,hubspot,dovetail"`.
 */
export type SmartlinkUsageAndTopApps = {
	/** Tenant-wide percentage of end users who have rendered smartlinks. */
	percentage: number;
	/** App ids ranked most → least used in that tenant. */
	mostUsedApps: string[];
};

const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorSmartlinkUsageAndTopApps,
	get: getSmartlinkUsageAndTopAppsCache,
	clear: clearSmartlinkUsageAndTopAppsCache,
} = promiseCache<Trait | undefined>({
	name: SEARCH_PERSONALIZATION_SMARTLINK_USAGE_AND_TOP_APPS_SITE_CACHE_KEY,
	expiration: SEVEN_DAY_MS_TTL,
});

const fetchSiteSmartlinkUsageAndTopAppsService = async (siteId: string): Promise<Traits> => {
	// Re-uses the existing TAP-delivery site traits endpoint that smartlink uses;
	// every site-scoped TAP trait is returned in the same `attributes` bag.
	const requestUrl = getSmartlinkSiteTraitsUrl(siteId);
	const requestOptions: RequestInit = {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
	};

	return fetch(requestUrl, requestOptions).then((response: Response) => {
		if (response.ok) {
			return response.json();
		}

		return Promise.reject({
			code: response.status,
			reason: response.statusText,
		});
	});
};

const fetchSmartlinkUsageAndTopAppsTrait = async ({
	siteId,
	analyticsCallback,
	cacheKeySuffix,
	degradationCallback = () => {},
}: {
	siteId: string;
	analyticsCallback?: CacheAnalyticsCallback;
	cacheKeySuffix?: string;
	degradationCallback?: DegradationCallback;
}) => {
	const key = generateHash(
		`${SEARCH_PERSONALIZATION_SMARTLINK_USAGE_AND_TOP_APPS_SITE_CACHE_KEY}|${siteId}|${cacheKeySuffix}`,
	);

	const supplier = async () => {
		const fetchPromise = fetchSiteSmartlinkUsageAndTopAppsService(siteId)
			.then((data: Traits) => {
				const { attributes } = data;
				return attributes?.find((t) => t.name === SMARTLINK_USAGE_AND_TOP_APPS_TRAIT);
			})
			.catch(() => undefined); // treat non-200 as empty and avoid caching a rejected promise

		const degrader = new DegradablePromise(fetchPromise, degradationCallback, 1000); // 1s degrade threshold

		return degrader.promise();
	};

	const existing = getSmartlinkUsageAndTopAppsCache(key.toString());
	// Choose TTL based on outcome:
	// - success (non-empty trait): default SEVEN_DAY_TTL
	// - empty (undefined): ONE_DAY_TTL
	// - degraded (timeout/reject): ONE_HOUR_TTL
	const effectiveExpiration = !existing?.storedValue ? ONE_HOUR_MS_TTL : undefined;
	const effectiveOnlyRefreshIfExpired = true;

	// Helper to re-write the cached value with a specific TTL without re-fetching
	const writeBackWithTTL = (value: Trait | undefined, ttl: number) => {
		refreshOnAccessDecoratorSmartlinkUsageAndTopApps({
			key: key.toString(),
			supplier: async () => value,
			returnExpired: false,
			onlyRefreshIfExpired: false, // force refresh to apply new TTL
			expiration: ttl,
		});
	};

	return refreshOnAccessDecoratorSmartlinkUsageAndTopApps({
		key: key.toString(),
		supplier: async () => {
			try {
				const result = await supplier(); // Trait | undefined (no degrade)
				if (result) {
					writeBackWithTTL(result, SEVEN_DAY_MS_TTL);
				} else {
					writeBackWithTTL(undefined, ONE_DAY_MS_TTL);
				}
				return result;
			} catch {
				// On degrade/timeout, return undefined so promiseCache stores an "empty" value
				// and then write back a short TTL to avoid hammering
				writeBackWithTTL(undefined, ONE_HOUR_MS_TTL);
				return undefined;
			}
		},
		onlyRefreshIfExpired: effectiveOnlyRefreshIfExpired,
		expiration: effectiveExpiration,
		analyticsCallback,
	});
};

// Mainly for testing, clearing cache
export const __test_clearSmartlinkUsageAndTopAppsCache = (key?: string): void =>
	clearSmartlinkUsageAndTopAppsCache(key);

/**
 * Parses the raw CSV value of the
 * `rovo_nudge_end_user_rendered_smartlinks_percent_and_most_used_apps` trait into
 * `{ percentage, mostUsedApps }`.
 *
 * Example: `"82,google-calendar,zendesk,drive,slack,miro,figma,hubspot,dovetail"` →
 * `{ percentage: 82, mostUsedApps: ['google-calendar', 'zendesk', 'drive', 'slack', 'miro', 'figma', 'hubspot', 'dovetail'] }`.
 *
 * Returns `undefined` when:
 * - the trait is missing,
 * - the trait name does not match,
 * - the trait value is not a string,
 * - or the leading int cannot be parsed (e.g. empty or non-numeric).
 *
 * Honours the "empty vs zero" gotcha: an unparseable percent collapses the
 * whole object to `undefined`, whereas a literal `"0,..."` yields
 * `{ percentage: 0, ... }`.
 */
export const parseSmartlinkUsageAndTopAppsTrait = (
	trait?: Trait,
): SmartlinkUsageAndTopApps | undefined => {
	if (trait?.name !== SMARTLINK_USAGE_AND_TOP_APPS_TRAIT || typeof trait?.value !== 'string') {
		return undefined;
	}

	const segments = trait.value.split(',');
	const [first, ...rest] = segments;
	const percentage = parseInt((first ?? '').trim(), 10);

	if (Number.isNaN(percentage)) {
		return undefined;
	}

	const mostUsedApps = rest.map((s) => s.trim()).filter(Boolean);

	return { percentage, mostUsedApps };
};

/**
 * Gets the tenant-wide percentage of end users who have rendered smartlinks
 * and the ranked list of most used apps for a site.
 *
 * Source: TAP site trait `rovo_nudge_end_user_rendered_smartlinks_percent_and_most_used_apps`.
 *
 * @param siteId - The site for which to fetch the trait.
 * @param analyticsCallback - Optional callback for cache analytics.
 * @param degradationCallback - Optional callback for handling degradation scenarios.
 * @returns A promise resolving to the parsed `SmartlinkUsageAndTopApps`,
 *          or `undefined` when the trait is missing/unparseable.
 */
export const getTenantSmartlinkUsageAndTopApps = async ({
	siteId,
	analyticsCallback,
	degradationCallback,
}: {
	siteId: string;
	analyticsCallback?: CacheAnalyticsCallback;
	degradationCallback?: DegradationCallback;
}): Promise<SmartlinkUsageAndTopApps | undefined> => {
	if (!siteId) {
		return undefined;
	}
	const trait = await fetchSmartlinkUsageAndTopAppsTrait({
		siteId,
		analyticsCallback,
		degradationCallback,
		cacheKeySuffix: 'rovo-search-smartlink-usage-and-top-apps',
	});

	return parseSmartlinkUsageAndTopAppsTrait(trait);
};
